// utils/variants.js
//
// Variant key generation and lookup.
//
// Contract (from backend docs):
//   • Sort attribute names alphabetically (case-sensitive).
//   • Join as `name=value` pairs separated by `|`.
//   • Do NOT URL-encode.
//   • Example: { Size: "L", Color: "Red" } → "Color=Red|Size=L"
//
// Every consumer in the app uses these functions so keys generated on
// the admin side always match keys looked up on the buyer side.

/* ══════════════════════════════════════════════════════════
   Key building
   ══════════════════════════════════════════════════════════ */

/**
 * Build a stable variant key from an attributes object.
 *
 * @param {Object<string,string>} attributes  e.g. { Size: "L", Color: "Red" }
 * @returns {string}                          e.g. "Color=Red|Size=L"
 */
export const buildVariantKey = (attributes) => {
  if (!attributes || typeof attributes !== "object") return "";
  const entries = Object.entries(attributes).filter(
    ([k, v]) => k != null && v != null && String(k).length && String(v).length
  );
  if (entries.length === 0) return "";
  entries.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return entries.map(([k, v]) => `${k}=${v}`).join("|");
};

/**
 * Parse a variant key back into an attributes object.
 * Useful for debugging and for the buyer-side selector.
 *
 * @param {string} key  e.g. "Color=Red|Size=L"
 * @returns {Object<string,string>}
 */
export const parseVariantKey = (key) => {
  if (!key || typeof key !== "string") return {};
  const out = {};
  key.split("|").forEach((pair) => {
    const eq = pair.indexOf("=");
    if (eq <= 0) return;
    const name = pair.slice(0, eq).trim();
    const value = pair.slice(eq + 1).trim();
    if (name && value) out[name] = value;
  });
  return out;
};

/* ══════════════════════════════════════════════════════════
   Combination generation
   ══════════════════════════════════════════════════════════ */

/**
 * Generate every combination of the given attribute values.
 *
 * @param {Object<string,string[]>} attributeValues
 *   e.g. { Size: ["S","M","L"], Color: ["Red","Yellow"] }
 * @returns {Object<string,string>[]}  cartesian product as attribute maps
 *
 * Combination order: the attribute with the FEWEST values iterates
 * slowest, matching a typical pricing grid where you tab through the
 * smaller axis. Deterministic, but any stable order works since the
 * key is what matters downstream.
 */
export const generateCombinations = (attributeValues) => {
  if (!attributeValues || typeof attributeValues !== "object") return [];
  const names = Object.keys(attributeValues).filter(
    (n) => Array.isArray(attributeValues[n]) && attributeValues[n].length > 0
  );
  if (names.length === 0) return [];

  // Smallest last → it becomes the inner (fastest) loop in the reduce.
  names.sort((a, b) => attributeValues[b].length - attributeValues[a].length);

  return names.reduce(
    (acc, name) => {
      const values = attributeValues[name];
      const next = [];
      acc.forEach((partial) => {
        values.forEach((v) => {
          next.push({ ...partial, [name]: v });
        });
      });
      return next;
    },
    [{}]
  );
};

/* ══════════════════════════════════════════════════════════
   Lookup
   ══════════════════════════════════════════════════════════ */

/**
 * Find a variant in an array by its key.
 *
 * @param {Array} variants    array of { key, ... }
 * @param {string} key
 * @returns {Object|null}
 */
export const findVariantByKey = (variants, key) => {
  if (!Array.isArray(variants) || !key) return null;
  return variants.find((v) => v && v.key === key) || null;
};

/**
 * Given the current per-attribute selection (partial), compute the
 * full key that would result if a value is chosen for every attribute.
 * Returns null if the selection is incomplete.
 *
 * @param {Object<string,string>} selection  e.g. { Size: "L", Color: "Red" }
 * @param {string[]} attributeNames          e.g. ["Size", "Color"]
 * @returns {string|null}
 */
export const keyFromSelection = (selection, attributeNames) => {
  if (!selection || !Array.isArray(attributeNames)) return null;
  const complete = attributeNames.every((n) => selection[n]);
  if (!complete) return null;
  return buildVariantKey(selection);
};

/* ══════════════════════════════════════════════════════════
   Attribute introspection
   ══════════════════════════════════════════════════════════ */

/**
 * Union of attribute names and their possible values across all variants.
 * Preserves the order in `declaredAttributes` when provided.
 *
 * @param {Array} variants                array of { attributes }
 * @param {string[]} [declaredAttributes] preferred ordering
 * @returns {{ names: string[], valuesByName: Object<string,string[]> }}
 */
export const collectAttributeValues = (variants, declaredAttributes) => {
  const valuesByName = {};
  (variants || []).forEach((v) => {
    if (!v || !v.attributes) return;
    Object.entries(v.attributes).forEach(([name, value]) => {
      if (!name || value == null || !String(value).length) return;
      if (!valuesByName[name]) valuesByName[name] = [];
      if (!valuesByName[name].includes(value)) valuesByName[name].push(value);
    });
  });

  const fromVariants = Object.keys(valuesByName);
  const names =
    Array.isArray(declaredAttributes) && declaredAttributes.length > 0
      ? [
          ...declaredAttributes.filter((n) => fromVariants.includes(n)),
          ...fromVariants.filter((n) => !declaredAttributes.includes(n)),
        ]
      : fromVariants;

  return { names, valuesByName };
};

/**
 * For a given attribute and selection, which of its values are
 * available given the OTHER attributes' current picks?
 *
 * Used to grey out values in the buyer-side selector that would
 * lead to a non-existent or out-of-stock combination.
 *
 * @param {Array} variants               array of { key, attributes, is_in_stock, stock }
 * @param {string} attributeName         the attribute whose values we want
 * @param {Object<string,string>} selection  current picks (may omit this attribute)
 * @param {string[]} allAttributeNames   every attribute name
 * @returns {Object<string, boolean>}    map of value → is available
 */
export const availabilityMap = (
  variants,
  attributeName,
  selection,
  allAttributeNames
) => {
  const out = {};
  if (!Array.isArray(variants) || !attributeName) return out;

  const otherNames = allAttributeNames.filter((n) => n !== attributeName);
  const candidates = variants.filter((v) =>
    otherNames.every((n) => !selection[n] || v.attributes?.[n] === selection[n])
  );

  candidates.forEach((v) => {
    const val = v.attributes?.[attributeName];
    if (!val) return;
    const avail =
      v.is_in_stock !== false &&
      (v.stock == null || v.stock === undefined || Number(v.stock) > 0);
    out[val] = out[val] || avail;
  });

  return out;
};

/* ══════════════════════════════════════════════════════════
   Validation
   ══════════════════════════════════════════════════════════ */

/**
 * Validate a variant grid before submitting to the backend.
 * Returns { valid: boolean, errors: string[] }.
 *
 * Rules:
 *   • Every variant needs a key.
 *   • No duplicate keys.
 *   • Every variant needs base_price_customer and base_price_agent.
 *   • If saleType is PRE_ORDER or BOTH, each variant needs pre-order prices.
 */
export const validateVariants = (variants, saleType) => {
  const errors = [];
  if (!Array.isArray(variants) || variants.length === 0) {
    return { valid: false, errors: ["At least one variant is required."] };
  }

  const seen = new Set();
  const needsPreorder = saleType === "PRE_ORDER" || saleType === "BOTH";

  variants.forEach((v, i) => {
    const label = v.key || `Variant #${i + 1}`;
    if (!v.key) {
      errors.push(`${label}: missing key.`);
    } else if (seen.has(v.key)) {
      errors.push(`${label}: duplicate key.`);
    } else {
      seen.add(v.key);
    }

    const c = parseFloat(v.base_price_customer);
    const a = parseFloat(v.base_price_agent);

    if (v.base_price_customer === "" || v.base_price_customer == null || isNaN(c) || c < 0) {
      errors.push(`${label}: customer price required.`);
    }
    if (v.base_price_agent === "" || v.base_price_agent == null || isNaN(a) || a < 0) {
      errors.push(`${label}: agent price required.`);
    }

    if (needsPreorder) {
      const pc = parseFloat(v.pre_order_price_customer);
      const pa = parseFloat(v.pre_order_price_agent);
      if (v.pre_order_price_customer === "" || v.pre_order_price_customer == null || isNaN(pc)) {
        errors.push(`${label}: pre-order customer price required.`);
      }
      if (v.pre_order_price_agent === "" || v.pre_order_price_agent == null || isNaN(pa)) {
        errors.push(`${label}: pre-order agent price required.`);
      }
    }
  });

  return { valid: errors.length === 0, errors };
};

/**
 * Given a set of variants, return the minimum customer and agent base
 * prices. Used to auto-fill the parent product's fallback prices.
 */
export const minBasePrices = (variants) => {
  let minC = null;
  let minA = null;
  (variants || []).forEach((v) => {
    const c = parseFloat(v.base_price_customer);
    const a = parseFloat(v.base_price_agent);
    if (!isNaN(c) && (minC === null || c < minC)) minC = c;
    if (!isNaN(a) && (minA === null || a < minA)) minA = a;
  });
  return {
    customer: minC === null ? "" : String(minC),
    agent: minA === null ? "" : String(minA),
  };
};









/* ══════════════════════════════════════════════════════════
   Wholesale / MOQ
   ══════════════════════════════════════════════════════════ */

/**
 * True when the product is a wholesale item that requires a minimum
 * order quantity. Returns false for non-wholesale or malformed products.
 *
 * @param {object} product
 * @returns {boolean}
 */
export const isWholesale = (product) =>
  !!product?.is_wholesale && Number(product?.moq) >= 2;

/**
 * The minimum quantity a buyer must add. Defaults to 1 for regular
 * products so callers can always treat `minQty` as a hard floor.
 *
 * @param {object} product
 * @returns {number}
 */
export const minOrderQty = (product) =>
  isWholesale(product) ? Number(product.moq) : 1;

/**
 * Clamp a requested quantity so it always meets the product's MOQ.
 * Used on quantity change handlers to prevent the buyer from ever
 * landing on a value the backend will reject.
 *
 * @param {object} product
 * @param {number} qty
 * @returns {number}
 */
export const clampToMoq = (product, qty) => {
  const min = minOrderQty(product);
  const n = Number(qty) || min;
  return Math.max(min, n);
};

/**
 * Short label for wholesale badges, e.g. "Min 10 units" or null.
 *
 * @param {object} product
 * @returns {string|null}
 */
export const moqLabel = (product) =>
  isWholesale(product) ? `Min ${Number(product.moq)} units` : null;